import apiClient from './AuthService';

export interface ProjectGroup {
  projectGroupId: string;
  groupName: string;
  description?: string;
  progress: number;
  totalContents: number;
  completedContents: number;
}

export interface ProjectSection {
  id: string;
  name: string;
  order: number;
  isCompleted: boolean;
  contents: SectionContent[];
}

export interface SectionContent {
  id: string;
  type: 'question' | 'text' | 'media';
  title?: string;
  content: any;
  order: number;
  isCompleted: boolean;
}

export interface ProjectMember {
  memberId: string;
  projectId: string;
  memberType: 'HUMAN' | 'PET';
  name: string;
  age: number | null;
  petType: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserProjectResponse {
  projectId: string;
  internalName: string;
  externalName: string;
  interventionType: string;
  surface: string;
  clientEmail: string;
  invitationCode: number;
  clientSize: string;
  clientTone: string;
  ownerId: string;
  clientId: string;
  createdAt: string;
  updatedAt: string;
  projectProgress: number;
  projectTotalContents: number;
  projectCompletedContents: number;
  groups: ProjectGroup[];
}

export interface ProjectDetail {
  id: string;
  name: string;
  description?: string;
  sections: ProjectSection[];
  totalQuestions: number;
  completedQuestions: number;
  progress: number;
}

export interface GroupQuizData {
  group: {
    id: string;
    name: string;
    subtitle?: string;
    timeDuration?: any;
    progress: number;
    sections?: ProjectSection[];
    totalQuestions?: number;
    completedQuestions?: number;
  };
  project?: {
    clientSize: 'SINGULAR' | 'PLURAL' | string;
    clientTone: 'INFORMAL' | 'FORMAL' | string;
  };
}

export const getUserProject = async (): Promise<UserProjectResponse> => {
  try {
    const response = await apiClient.get<UserProjectResponse>('/quiz/client/project');
    return response.data;
  } catch (error) {
    console.error('Error fetching user project:', error);
    throw error;
  }
};

export const getProjectDetail = async (projectId: string): Promise<ProjectDetail> => {
  try {
    const response = await apiClient.get<ProjectDetail>(`/quiz/client/project/${projectId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching project detail for ${projectId}:`, error);
    throw error;
  }
};

export const getSectionContent = async (projectId: string, sectionId: string): Promise<SectionContent[]> => {
  try {
    const response = await apiClient.get<SectionContent[]>(`/quiz/client/project/${projectId}/section/${sectionId}/content`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching section content for project ${projectId}, section ${sectionId}:`, error);
    throw error;
  }
};

export const saveUserResponse = async (
  projectId: string, 
  sectionId: string, 
  contentId: string, 
  response: any
): Promise<void> => {
  try {
    await apiClient.post(`/quiz/client/project/${projectId}/section/${sectionId}/content/${contentId}/response`, {
      response
    });
  } catch (error) {
    console.error('Error saving user response:', error);
    throw error;
  }
};

export const getGroupQuizData = async (projectGroupId: string): Promise<GroupQuizData> => {
  try {
    const response = await apiClient.get<GroupQuizData>(`/quiz/quiz/group/${projectGroupId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching group quiz data for ${projectGroupId}:`, error);
    throw error;
  }
};

export const getUserProgress = async (projectId: string): Promise<{ completedSections: string[], totalSections: number }> => {
  try {
    const response = await apiClient.get(`/quiz/client/project/${projectId}/progress`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching user progress for project ${projectId}:`, error);
    throw error;
  }
};

export const saveQuizResponse = async (
  projectId: string,
  projectSectionContentId: string,
  responseContent: string,
  responseType: 'text' | 'single_choice' | 'multiple_choice' | 'numeric' | 'boolean'
): Promise<void> => {
  try {
    await apiClient.post(`/quiz/projects/${projectId}/responses`, {
      project_section_content_id: projectSectionContentId,
      response_content: responseContent,
      response_type: responseType
    });
  } catch (error) {
    console.error(`Error saving quiz response for project ${projectId}:`, error);
    throw error;
  }
};

export const markContentAsCompleted = async (sectionContentId: string): Promise<void> => {
  if (!sectionContentId || sectionContentId.trim() === '') {
    throw new Error('sectionContentId is required and cannot be empty');
  }

  try {
    console.log(`Attempting to mark content ${sectionContentId} as completed`);
    const response = await apiClient.put(`/quiz/content/${sectionContentId}/complete`, {
      is_completed: true
    });
    console.log(`Successfully marked content ${sectionContentId} as completed`, response.data);
  } catch (error: any) {
    console.error(`Error marking content ${sectionContentId} as completed:`, error);

    if (error.response) {
      // El servidor respondió con un código de estado de error
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);

      if (error.response.status === 404) {
        throw new Error(`Content with ID ${sectionContentId} not found`);
      } else if (error.response.status === 401) {
        throw new Error('Authentication required to mark content as completed');
      } else if (error.response.status === 403) {
        throw new Error('Not authorized to mark this content as completed');
      }
    } else if (error.request) {
      // La petición fue hecha pero no se recibió respuesta
      console.error('Request made but no response received:', error.request);
      throw new Error('Network error: Unable to reach server');
    } else {
      // Algo pasó al configurar la petición
      console.error('Error setting up request:', error.message);
    }

    throw error;
  }
};

export interface ContentResponse {
  quizResponseId?: string;
  projectId?: string;
  projectSectionId: string;
  projectSectionContentId?: string;
  respondentId?: string;
  responseContent: string;
  responseType: string;
  sectionName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const getContentResponse = async (projectSectionContentId: string): Promise<ContentResponse | null> => {
  try {
    const response = await apiClient.get(`/quiz/content/${projectSectionContentId}/response`);

    // Handle both wrapped and direct response formats
    const data = response.data.data || response.data;

    // Map API response fields to our interface
    return {
      quizResponseId: data.quizResponseId,
      projectId: data.projectId,
      projectSectionId: data.project_section_id || data.projectSectionId,
      projectSectionContentId: projectSectionContentId, // We know this from the request
      respondentId: data.respondentId,
      responseContent: data.response_content || data.responseContent,
      responseType: data.response_type || data.responseType,
      sectionName: data.sectionName,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt
    };
  } catch (error: any) {
    // If no response found, return null instead of throwing error
    if (error.response?.status === 404 || error.response?.data?.error === "No response found for this content") {
      return null;
    }
    console.error(`Error fetching content response for ${projectSectionContentId}:`, error);
    throw error;
  }
};

// Get project members
export const getProjectMembers = async (projectId: string): Promise<ProjectMember[]> => {
  try {
    const response = await apiClient.get(`/quiz/project/${projectId}/members`);
    return response.data.members || [];
  } catch (error: any) {
    console.error(`Error fetching members for project ${projectId}:`, error);
    throw error;
  }
};

// Create project member
export const createProjectMember = async (
  projectId: string,
  memberData: {
    name: string;
    member_type?: 'HUMAN' | 'PET';
    age?: number;
    pet_type?: string;
  }
): Promise<{ member_id: string; message: string }> => {
  try {
    const response = await apiClient.post(`/quiz/project/${projectId}/member`, memberData);
    return response.data;
  } catch (error: any) {
    console.error(`Error creating member for project ${projectId}:`, error);
    throw error;
  }
};

// Delete project member
export const deleteProjectMember = async (memberId: string): Promise<{ message: string }> => {
  try {
    const response = await apiClient.delete(`/quiz/member/${memberId}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error deleting member ${memberId}:`, error);
    throw error;
  }
};